import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import CollapsibleText from "app/components/CollapsibleText";
import EditProfileForm from "app/components/EditProfile/EditProfileForm";
import { PageContainer } from "app/components/PageContainer";
import { awsImageRootUrl } from "lib/constants";
import { Category } from "lib/features/category/categoryService";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useAppSelector } from "lib/hooks";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import authServices, { UserCustomerType } from "services/auth-service";
import { profileStyles } from "styles/RefereeDirectory/profile.styles";
import { formatSinceDate } from "util/formatDate";
import BookingLinkShareIcon from "../../assets/icons/booking_link_share_icon.svg";
import SuccessfulBookingsIcon from "../../assets/icons/successful_bookings_icon.svg";
import VerticalBarsIcon from "../../assets/icons/vertical_bars.svg";

import chatServices from "services/chat-service";
import MessageIcon from "../../assets/icons/light_message.svg";
import LocationIcon from "../../assets/icons/location_pin.svg";
import PencilIcon from "../../assets/icons/pencil.svg";
import SuitcaseIcon from "../../assets/icons/suitcase.svg";
import WorkplaceIcon from "../../assets/icons/user_workplace.svg";

interface ProfileInfoItemProps {
  icon: string;
  alt: string;
  text: string;
}
const ProfileInfoItem = ({ icon, alt, text }: ProfileInfoItemProps) => (
  <Box sx={profileStyles.profileInfoItem}>
    <Box
      component="img"
      src={icon}
      alt={alt}
      sx={profileStyles.profileInfoIcon}
    />
    <Typography sx={profileStyles.profileInfoText}>{text}</Typography>
  </Box>
);

interface MetricItemProps {
  icon: string;
  alt: string;
  label: string;
  value?: string | number;
}

const MetricItem: React.FC<MetricItemProps> = ({ icon, alt, label, value }) => {
  return (
    <Box sx={profileStyles.metricItemContainer}>
      <Box sx={profileStyles.metricItemInnerContainer}>
        <Box sx={profileStyles.metricItemContent}>
          <img src={icon} width={20} height={20} alt={alt} />
          <Typography sx={profileStyles.metricItemLabel}>{label}</Typography>
        </Box>
        <Typography sx={profileStyles.metricItemValue}>{value}</Typography>
      </Box>
    </Box>
  );
};

interface CreateConversationDto {
  participants: string[];
  initialMessage: {
    content: string;
    authorId: string;
  };
}
export default function Profile() {
  const { id } = useParams();
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState<User>();
  const [userStatistics, setUserStatistics] = useState<any>();

  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize SendbirdChat instance


    const onPressCreateChannel = async (event: React.MouseEvent) => {
    
    event.stopPropagation();
    let channelUrl = "";

    if (user?.id  && userDetails?.id) {
      console.log("Creating channel between users:", user.id, id);
      const payload: CreateConversationDto = {  
        participants: [user.id.toString(), id?.toString() || ""],
        initialMessage: {
          content: "Connecting via Refereree",
          authorId: user.id.toString(),
        },
      };
      
      setLoading(true);
      const response = await chatServices.createChannel(payload);
      console.log("Create channel response:", response);
      channelUrl = response?.data?.conversation;
      console.log("Navigating to channelUrl:", channelUrl);
      if (channelUrl) {
        setLoading(false);
        navigate(`/chat/${channelUrl}`);
        return;
      } else {
      }
    }
  };

  const fetchUser = async () => {
    if (id) {
      try {
        setLoader(true);
        const response = await authServices.fetchUser(Number(id));
        setUserDetails(response?.data);
      } catch (error: any) {
        setError(error?.message || "Internal Server Error");
      } finally {
        setLoader(false);
      }
    }
  };

  const fetchUserStatistics = async () => {
    if (id) {
      try {
        const response = await authServices.fetchUserStatistics(Number(id));
        setUserStatistics(response?.data);
      } catch (error: any) {
        setError(error?.message || "Internal Server Error");
      }
    }
  };

  useEffect(() => {
    fetchUser();
    fetchUserStatistics();
  }, [id]);

  if (loader)
    return (
      <Box sx={profileStyles.loaderContainer}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={profileStyles.errorContainer}>
        <Typography sx={profileStyles.errorText}>{error}</Typography>
      </Box>
    );

  return (
    <PageContainer>
      {isEditing && userDetails ? (
        <EditProfileForm
          data={userDetails}
          setIsEditing={setIsEditing}
          fetchUser={fetchUser}
        />
      ) : (
        <Box sx={profileStyles.container}>
          <Box sx={profileStyles.avatarContainer}>
            <img
              src={`${awsImageRootUrl}/${userDetails?.avatar}`}
              alt="Business Logo"
              style={{
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                objectFit: "cover",
                margin: "0 auto",
              }}
            />
          </Box>

          <Box sx={{ marginTop: "1rem" }}>
            <Typography sx={profileStyles.nameText}>
              {userDetails?.first_name + " " + userDetails?.last_name}
            </Typography>

            {userDetails?.influencer_type?.type && (
              <ProfileInfoItem
                icon={SuitcaseIcon}
                alt="suitcase-icon"
                text={
                  userDetails?.influencer_type?.id === 10
                    ? userDetails?.job_type || ""
                    : userDetails?.influencer_type.type
                }
              />
            )}

            {userDetails?.work_place && (
              <ProfileInfoItem
                icon={WorkplaceIcon}
                alt="workplace-icon"
                text={userDetails?.work_place}
              />
            )}

            {userDetails?.regions?.[0].region?.region && (
              <ProfileInfoItem
                icon={LocationIcon}
                alt="location-icon"
                text={userDetails?.regions?.[0].region?.region}
              />
            )}

            {userDetails?.id === user?.id ? (
              <Button
                variant="contained"
                sx={profileStyles.profileButton}
                onClick={() => setIsEditing(true)}
              >
                <img src={PencilIcon} alt="pencil-icon" />
                Edit Profile
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={profileStyles.profileButton}
                onClick={onPressCreateChannel}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{ color: "white", paddingY: "3px" }}
                  />
                ) : (
                  <>
                    <img src={MessageIcon} alt="message-icon" />
                    Message
                  </>
                )}
              </Button>
            )}
          </Box>

          {/* About Me Container */}
          {userDetails?.about_me && (
            <Box sx={profileStyles.aboutContainer}>
              <Typography sx={profileStyles.aboutTitle}>About</Typography>
              <CollapsibleText limit={140} text={userDetails?.about_me} />
            </Box>
          )}

          {/* Customer Type Id */}
          {userDetails?.customer_types &&
            userDetails?.customer_types?.length > 0 && (
              <Box sx={profileStyles.aboutContainer}>
                <Typography sx={profileStyles.aboutTitle}>
                  {" "}
                  Most common customer types:{" "}
                </Typography>
                <Box mt={1}>
                  {userDetails?.customer_types &&
                    userDetails?.customer_types?.map(
                      (customer: UserCustomerType) => {
                        return (
                          <Chip
                            key={customer.id}
                            label={customer.type}
                            variant={"outlined"}
                            color={"default"}
                            sx={{
                              margin: "0 0.5rem 0.5rem 0",
                              border: "1px solid #000",
                            }}
                          />
                        );
                      }
                    )}
                </Box>
              </Box>
            )}

          {/* Customer Categories */}
          {userDetails?.customer_types &&
            userDetails?.customer_types?.length > 0 && (
              <Box sx={profileStyles.aboutContainer}>
                <Typography sx={profileStyles.aboutTitle}>
                  {" "}
                  Most likely to recommend:{" "}
                </Typography>
                <Box mt={1}>
                  {userDetails?.categories &&
                    userDetails?.categories?.map((customer: Category) => {
                      return (
                        <Chip
                          key={customer.id}
                          label={customer.category}
                          variant={"outlined"}
                          color={"default"}
                          sx={{
                            margin: "0 0.5rem 0.5rem 0",
                            border: "1px solid #000",
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>
            )}

          {/* Metrices Container */}
          <Box>
            <Typography
              sx={{ fontSize: "22px", fontWeight: "600", marginTop: "25px" }}
            >
              Metrices
            </Typography>

            {userDetails?.created_time && (
              <Typography sx={profileStyles.italicText}>
                Referee since ({formatSinceDate(userDetails?.created_time)}){" "}
              </Typography>
            )}
          </Box>

          {userStatistics && (
            <Box sx={profileStyles.statsContainer}>
              {/* Booking Links Shared */}
              <Box sx={profileStyles.statBox}>
                <Typography sx={profileStyles.statsHeading}>
                  Booking Link Shares
                </Typography>
                <Box sx={profileStyles.statsBoxFooter}>
                  <img
                    src={BookingLinkShareIcon}
                    width={32}
                    height={32}
                    alt="booking-icon"
                  />
                  <Typography sx={profileStyles.statsNumber}>
                    {userStatistics?.referral}
                  </Typography>
                </Box>
              </Box>

              {/* Complete Bookings */}
              <Box sx={profileStyles.statBox}>
                <Typography sx={profileStyles.statsHeading}>
                  Completed Bookings
                </Typography>
                <Box sx={profileStyles.statsBoxFooter}>
                  <img
                    src={SuccessfulBookingsIcon}
                    width={32}
                    height={32}
                    alt="success-icon"
                  />
                  <Typography sx={profileStyles.statsNumber}>
                    {userStatistics?.booking}
                  </Typography>
                </Box>
              </Box>

              {/* Top Categories Referred */}
              {userStatistics?.topCategories &&
                Object.entries(userStatistics.topCategories)?.length > 0 && (
                  <Box sx={profileStyles.statBox}>
                    <Typography sx={profileStyles.statsHeading}>
                      Top Categories Referred
                    </Typography>
                    <Box sx={profileStyles.statsBoxFooter}>
                      <Box>
                        {Object.entries(userStatistics.topCategories).map(
                          ([category], index) => (
                            <Typography
                              key={index}
                              sx={profileStyles.statsCategory}
                            >
                              • {category}
                            </Typography>
                          )
                        )}
                      </Box>
                      <img src={VerticalBarsIcon} alt="top-icon" />
                    </Box>
                  </Box>
                )}
            </Box>
          )}
        </Box>
      )}
    </PageContainer>
  );
}
